import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  IconButton,
  Tag,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import {
  DocumentVersionFieldFragment,
  ExternalIngestionDocumentFragment,
  ExternalIngestionFolderFragment,
  ExternalValidationReportDataFragment,
  GetIngestionDocumentFragment,
  IngestionDocumentReviewFragment,
  IngestionDocumentReviewStatus,
  IngestionDocumentValidationErrorReviewFragment,
  InternalGetIngestionDocumentFragment,
  InternalIngestionFolderFragment,
  TxPipelineNeedsInvestigationReason,
} from '@client/graphql/__generated__/types';
import { Rabbit, X } from 'lucide-react';
import { FC, Fragment, useCallback, useMemo, useState } from 'react';
import { ClassificationDetails } from '~/apps/internal/pages/InternalCompliance/components/Classification/ClassificationDetails';
import { CenterSpinner } from '~/common/components/CenterSpinner';
import { FieldImagesFromPdf } from '~/common/components/DocumentPDF/FieldImagesFromPdf';
import { GradientIcon } from '~/common/components/GradientIcon';
import { useFieldImages } from '~/common/hooks/useFieldImages';
import { filterFieldsWithValidationResults } from '~/common/utils/validation.utils';
import { ComplianceValidationVariant } from './ComplianceDrawer';
import {
  AddIssueModal,
  IssueFormData,
  IssueType,
} from './components/AddIssueModal';
import { AIComparisonMetrics } from './components/AIComparisonMetrics';
import { BaseValidationItem } from './components/BaseValidationItem';
import { CollapsibleSection } from './components/CollapsibleSection';
import { CopyValidationErrors } from './components/CopyValidationErrors';
import { DebugInfoMetrics } from './components/DebugInfoMetrics';
import { ExtractedFieldsList } from './components/ExtractedFieldsList';
import { FolderInformation } from './components/FolderInformation';
import { InternalValidationItem } from './components/InternalValidationItem';
import {
  PipelineStageSelectionInfo,
  PipelineStageSelectionTooltipContent,
} from './components/PipelineStageSelectionInfo';
import { TimestampMetrics } from './components/TimestampMetrics';
import { useComplianceFeedback } from './hooks/useIngestionDocumentFeedback';
import { useSectionStates } from './hooks/useSectionStates';
import { KeyTermsCard } from './KeyTermsCard';
import { getVisibleIssuesCount } from './utils/ingestionDocuments.utils';
import {
  getFieldErrorRuleCountByContract,
  getPipelineTargetingByContract,
} from './utils/rules.utils';
import {
  getContractDocDisplayName,
  getValidationComparisonData,
  getValidationForDocument,
  getValidationResultsForDocument,
  getValidationTimestamps,
  ValidationItem,
} from './utils/validation.utils';
import {
  getValidationItemDiffData,
  hasValidationItemDifferences,
  ValidationItemDiffData,
} from './utils/validationItem.utils';

export interface ManualErrorGroup {
  document: GetIngestionDocumentFragment | ExternalIngestionDocumentFragment;
  contractDocumentUuid?: string;
  addedAt: number;
}

interface BaseComplianceValidationSidebarProps {
  variant: ComplianceValidationVariant;
  loading: boolean;
  isExtension?: boolean;
  selectedContractDocumentId?: string | null;
  validationData?: ExternalValidationReportDataFragment | null;
  hiddenDocumentValidationReviews?: IngestionDocumentValidationErrorReviewFragment[];
  userAddedDocumentIssues?: IngestionDocumentValidationErrorReviewFragment[];
  manualErrorGroups?: ManualErrorGroup[];
  reviewedDocuments?: { document: { id: string } }[];
  width?: string;
  onValidationFieldClick: (
    mappingKeys: string[],
    contractDocumentId?: string,
    isError?: boolean
  ) => void;
  onHideDocumentValidation?: (
    isHidden: boolean,
    message?: string,
    ingestionDocumentValidationId?: string,
    ruleUuid?: string,
    contractDocumentUuid?: string
  ) => void | Promise<void>;
  onAddDocumentIssue?: (
    issueData: IssueFormData,
    contractDocumentUuid?: string
  ) => void | Promise<void>;
  onDeleteDocumentIssue?: (reviewId: string) => void | Promise<void>;
  onCreateOrUpdateDocumentReview?: (
    ingestionDocumentId: string,
    status: IngestionDocumentReviewStatus,
    version?: string,
    reviewId?: string
  ) => void | Promise<void>;
  onShowHideValidationErrors?: (show: boolean) => void;
  onAddErrorGroupToMessage?: (
    document: GetIngestionDocumentFragment | ExternalIngestionDocumentFragment,
    contractDocumentUuid?: string
  ) => void;
  currentDocumentReview?: IngestionDocumentReviewFragment | null;
  submittedDocumentReviews?: IngestionDocumentReviewFragment[];
}

interface ExternalComplianceValidationSidebarProps
  extends BaseComplianceValidationSidebarProps {
  variant: 'external';
  folder?: ExternalIngestionFolderFragment | null;
  selectedDocument?: GetIngestionDocumentFragment | null;
}

interface InternalComplianceValidationSidebarProps
  extends BaseComplianceValidationSidebarProps {
  variant: 'internal';
  folder: InternalIngestionFolderFragment | null;
  selectedDocument?: InternalGetIngestionDocumentFragment | null;
}

export type ComplianceValidationSidebarProps =
  | ExternalComplianceValidationSidebarProps
  | InternalComplianceValidationSidebarProps;

export const ComplianceValidationSidebar: FC<
  ComplianceValidationSidebarProps
> = ({
  folder,
  variant,
  loading,
  isExtension = false,
  selectedDocument,
  validationData,
  hiddenDocumentValidationReviews = [],
  userAddedDocumentIssues = [],
  manualErrorGroups = [],
  reviewedDocuments = [],
  onValidationFieldClick,
  onHideDocumentValidation,
  onAddDocumentIssue,
  onDeleteDocumentIssue,
  onCreateOrUpdateDocumentReview,
  onShowHideValidationErrors,
  onAddErrorGroupToMessage,
  currentDocumentReview,
  submittedDocumentReviews,
  width = '350px',
}) => {
  // Find submitted review for the current document (as fallback when no unsubmitted review)
  const submittedDocumentReview = useMemo(() => {
    if (!selectedDocument || !submittedDocumentReviews) return null;

    return (
      submittedDocumentReviews.find(
        (review) => review.ingestionDocument.id === selectedDocument.id
      ) || null
    );
  }, [selectedDocument, submittedDocumentReviews]);

  // Determine if we should show review buttons
  const shouldShowReviewButtons = useMemo(() => {
    return (
      variant === 'external' &&
      onCreateOrUpdateDocumentReview &&
      selectedDocument &&
      (selectedDocument.needsReview || submittedDocumentReview !== null)
    );
  }, [
    variant,
    onCreateOrUpdateDocumentReview,
    selectedDocument,
    submittedDocumentReview,
  ]);

  // Use unsubmitted review if available, otherwise use submitted review for button state
  const reviewForButtonState = useMemo(() => {
    return currentDocumentReview || submittedDocumentReview;
  }, [currentDocumentReview, submittedDocumentReview]);

  const shouldAutoExpandPipeline = useMemo(() => {
    if (variant !== 'internal' || !folder) {
      return false;
    }

    const needsInvestigationReason =
      folder?.txPipelineNeedsInvestigationReason ??
      TxPipelineNeedsInvestigationReason.NONE;

    return needsInvestigationReason !== TxPipelineNeedsInvestigationReason.NONE;
  }, [folder, variant]);

  const { sectionStates, sectionActions } = useSectionStates(
    variant,
    shouldAutoExpandPipeline
  );
  const [hoveredRowIndex, setHoveredRowIndex] = useState<string | null>(null);
  const [isAddIssueModalOpen, setIsAddIssueModalOpen] = useState(false);
  const [
    selectedContractDocumentForIssue,
    setSelectedContractDocumentForIssue,
  ] = useState<string | null>(null);
  const { fieldsToImages, handleImagesLoaded } = useFieldImages();

  // Group user-added document issues by contract document
  const userAddedIssuesByContractDoc = useMemo(() => {
    const groupedIssues = new Map<
      string,
      IngestionDocumentValidationErrorReviewFragment[]
    >();

    userAddedDocumentIssues.forEach((issue) => {
      // Only include issues that have a contractDocumentUuid
      if (issue.contractDocumentUuid) {
        const contractDocId = issue.contractDocumentUuid;
        if (!groupedIssues.has(contractDocId)) {
          groupedIssues.set(contractDocId, []);
        }
        const issues = groupedIssues.get(contractDocId);
        if (issues) {
          issues.push(issue);
        }
      }
    });

    return groupedIssues;
  }, [userAddedDocumentIssues]);

  // Get validation results for all contract documents in the selected ingestion document
  const validationResultsForSelectedDocument = useMemo(() => {
    const resultsByContractDoc = new Map<
      string,
      {
        contractDocument: GetIngestionDocumentFragment['contractDocuments'][number];
        errors: ValidationItem[];
        successes: ValidationItem[];
        userAddedIssues: IngestionDocumentValidationErrorReviewFragment[];
        pageIssueMessage?: string | null;

        // Internal only
        pipelineTargetsDocument?: boolean;
        pipelineTargetsForm?: boolean;
        documentAndErrorRuleCount?: number;
        fieldErrorRuleCount?: number;
        isRequiredDocument?: boolean;
      }
    >();

    if (!selectedDocument || !selectedDocument.contractDocuments) {
      return resultsByContractDoc;
    }

    const docPageMessages = selectedDocument.docPageErrorMessage || {};

    selectedDocument.contractDocuments.forEach((contractDoc) => {
      const { errors: contractDocErrors, successes: contractDocSuccesses } =
        getValidationResultsForDocument(
          selectedDocument,
          validationData,
          contractDoc.id
        );

      const userAddedIssues =
        userAddedIssuesByContractDoc.get(contractDoc.id) || [];

      const rawPageIssueMessage = docPageMessages[contractDoc.id];
      const pageIssueMessage =
        typeof rawPageIssueMessage === 'string'
          ? rawPageIssueMessage.trim() || null
          : null;

      resultsByContractDoc.set(contractDoc.id, {
        contractDocument: contractDoc,
        errors: contractDocErrors,
        successes: contractDocSuccesses,
        userAddedIssues,
        pageIssueMessage,
      });
    });

    if (variant === 'internal') {
      const {
        byDocumentId,
        byForm,
        byDocumentIdAndErrorRulesCount,
        byDocumentIdAndRequiredDocument,
      } = getPipelineTargetingByContract(folder, selectedDocument);
      const fieldErrorRuleCountByContract =
        getFieldErrorRuleCountByContract(selectedDocument);

      resultsByContractDoc.forEach((result) => {
        result.pipelineTargetsDocument =
          byDocumentId.get(result.contractDocument.id) || false;
        result.pipelineTargetsForm =
          byForm.get(result.contractDocument.id) || false;
        result.documentAndErrorRuleCount =
          byDocumentIdAndErrorRulesCount.get(result.contractDocument.id) || 0;
        result.fieldErrorRuleCount =
          fieldErrorRuleCountByContract.get(result.contractDocument.id) || 0;
        result.isRequiredDocument =
          byDocumentIdAndRequiredDocument.get(result.contractDocument.id) ||
          false;
      });
    }

    return resultsByContractDoc;
  }, [
    variant,
    selectedDocument,
    folder,
    validationData,
    userAddedIssuesByContractDoc,
  ]);

  const emptyValidationsDescription: string = useMemo(() => {
    const defaultDescription = selectedDocument?.isArchived
      ? 'This document is archived and is not validated.'
      : 'No validations for this document';

    // [TODO] Add resolver fields to solve external cases
    if (variant !== 'internal') {
      return defaultDescription;
    }

    let rulesConfigured = false;
    for (const result of validationResultsForSelectedDocument.values()) {
      const hasDocLevelRules = (result.documentAndErrorRuleCount ?? 0) > 0;
      const hasPipelineLevelRules =
        (result.pipelineTargetsDocument || result.pipelineTargetsForm) &&
        (result.fieldErrorRuleCount ?? 0) > 0;

      if (hasDocLevelRules || hasPipelineLevelRules) {
        rulesConfigured = true;
        break;
      }
    }

    if (!rulesConfigured) {
      return 'No validations to run for this document at this stage';
    }

    return defaultDescription;
  }, [
    validationResultsForSelectedDocument,
    variant,
    selectedDocument?.isArchived,
  ]);

  // Get combined errors and successes for backward compatibility with existing hooks
  const { selectedDocErrors, selectedDocSuccesses } = useMemo(() => {
    const selectedDocErrors: ValidationItem[] = [];
    const selectedDocSuccesses: ValidationItem[] = [];

    validationResultsForSelectedDocument.forEach(({ errors, successes }) => {
      selectedDocErrors.push(...errors);
      selectedDocSuccesses.push(...successes);
    });

    return { selectedDocErrors, selectedDocSuccesses };
  }, [validationResultsForSelectedDocument]);

  // Get the first ingestion document validation ID
  const firstIngestionDocumentValidationId = useMemo(() => {
    const allItems = [...selectedDocErrors, ...selectedDocSuccesses];
    const firstItem = allItems.find(
      (item) => item.ingestionDocumentValidationId
    );

    return firstItem?.ingestionDocumentValidationId;
  }, [selectedDocErrors, selectedDocSuccesses]);

  // Get filtered validations for the selected document
  const validationForSelectedDocument = useMemo(() => {
    if (!selectedDocument) {
      return null;
    }

    return getValidationForDocument(selectedDocument, validationData);
  }, [selectedDocument, validationData]);

  // Get AI vs regular validation comparison data
  const comparisonData = useMemo(() => {
    if (
      variant !== 'internal' ||
      !selectedDocument ||
      !validationForSelectedDocument
    ) {
      return null;
    }

    return getValidationComparisonData(
      selectedDocument,
      validationForSelectedDocument
    );
  }, [selectedDocument, validationForSelectedDocument, variant]);

  const validationTimestamps = useMemo(() => {
    if (variant !== 'internal' || !validationForSelectedDocument) {
      return null;
    }

    return getValidationTimestamps(validationForSelectedDocument);
  }, [validationForSelectedDocument, variant]);

  const itemsDiffData = useMemo(() => {
    if (variant !== 'internal' || !selectedDocument || !validationData) {
      return undefined;
    }

    const allItems = [...selectedDocErrors, ...selectedDocSuccesses];
    const diffMap = new Map<string, ValidationItemDiffData[]>();

    allItems.forEach((item) => {
      const itemType = selectedDocErrors.includes(item) ? 'error' : 'success';
      const key = `${itemType}-${item.ruleUuid}`;

      if (
        hasValidationItemDifferences(item, selectedDocument, validationData)
      ) {
        const diffData = getValidationItemDiffData(
          item,
          selectedDocument,
          validationData
        );
        diffMap.set(key, diffData);
      }
    });

    return diffMap;
  }, [
    variant,
    selectedDocument,
    validationData,
    selectedDocErrors,
    selectedDocSuccesses,
  ]);

  // Get all document version fields for PDF image extraction (internal variant only)
  const allDocumentVersionFields = useMemo(() => {
    if (variant !== 'internal' || !selectedDocument) {
      return [];
    }

    const allItems = [...selectedDocErrors, ...selectedDocSuccesses];
    const allMappingKeys = new Set<string>();
    const fieldsByDocument = new Map<string, Set<string>>();

    allItems.forEach((item) => {
      if (item.inputValues && item.contractDocumentUuid) {
        item.inputValues.forEach((input) => {
          allMappingKeys.add(input.fieldKey);

          if (!fieldsByDocument.has(item.contractDocumentUuid!)) {
            fieldsByDocument.set(item.contractDocumentUuid!, new Set());
          }
          fieldsByDocument.get(item.contractDocumentUuid!)!.add(input.fieldKey);
        });
      }
    });

    // Collect all document version fields from all contract documents
    const allFields: DocumentVersionFieldFragment[] = [];

    if (selectedDocument.fields) {
      Object.entries(selectedDocument.fields).forEach(
        ([contractDocumentUuid, fields]) => {
          const documentFields =
            fields as unknown as DocumentVersionFieldFragment[];
          const relevantMappingKeys = Array.from(
            fieldsByDocument.get(contractDocumentUuid) || []
          );

          if (relevantMappingKeys.length > 0) {
            const filteredFields = filterFieldsWithValidationResults(
              documentFields,
              relevantMappingKeys
            );
            allFields.push(...filteredFields);
          }
        }
      );
    }

    return allFields;
  }, [variant, selectedDocument, selectedDocErrors, selectedDocSuccesses]);

  const { allFeedback } = useComplianceFeedback({
    firstIngestionDocumentValidationId,
    variant,
  });

  const handleItemClick = useCallback(
    (
      mappingKeys?: string[],
      contractDocumentUuid?: string,
      isError?: boolean
    ) => {
      if (mappingKeys?.length) {
        onValidationFieldClick(mappingKeys, contractDocumentUuid, isError);
      }
    },
    [onValidationFieldClick]
  );

  const handleOpenAddIssueModal = (contractDocumentUuid?: string) => {
    setSelectedContractDocumentForIssue(contractDocumentUuid || null);
    setIsAddIssueModalOpen(true);
  };

  const handleCloseAddIssueModal = () => {
    setIsAddIssueModalOpen(false);
    setSelectedContractDocumentForIssue(null);
  };

  const handleAddDocumentIssue = async (issueData: IssueFormData) => {
    if (onAddDocumentIssue) {
      await onAddDocumentIssue(
        issueData,
        selectedContractDocumentForIssue || undefined
      );
    }
    setIsAddIssueModalOpen(false);
    setSelectedContractDocumentForIssue(null);
  };

  const handleDeleteDocumentIssue = async (reviewId: string) => {
    if (onDeleteDocumentIssue) {
      await onDeleteDocumentIssue(reviewId);
    }
  };

  const handleReturnDocument = async () => {
    if (onCreateOrUpdateDocumentReview && selectedDocument) {
      await onCreateOrUpdateDocumentReview(
        selectedDocument.id,
        IngestionDocumentReviewStatus.RETURNED,
        selectedDocument.version,
        currentDocumentReview?.id
      );
    }
  };

  const handleApproveDocument = async () => {
    if (onCreateOrUpdateDocumentReview && selectedDocument) {
      await onCreateOrUpdateDocumentReview(
        selectedDocument.id,
        IngestionDocumentReviewStatus.APPROVED,
        selectedDocument.version,
        currentDocumentReview?.id
      );
    }
  };

  if (loading && !folder && !selectedDocument && !validationData) {
    return (
      <Box
        borderColor="compliance.sidebar.border"
        borderLeftWidth={1}
        height="100%"
        overflow="auto"
        p={4}
        w={width}
      >
        <Text color="compliance.loading.text">Loading validation data...</Text>
      </Box>
    );
  }

  if (!selectedDocument) {
    return (
      <Box
        borderColor="compliance.sidebar.border"
        borderLeftWidth={1}
        height="100%"
        overflow="auto"
        p={4}
        w={width}
      >
        <Text color="compliance.text.muted">
          Select a document to view validation results
        </Text>
      </Box>
    );
  }

  return (
    <Box
      borderColor="compliance.sidebar.border"
      height="100%"
      overflow="auto"
      p={3}
      w={width}
    >
      <VStack align="stretch" spacing={4}>
        {variant === 'internal' &&
          selectedDocument.fileUrl &&
          allDocumentVersionFields.length > 0 && (
            <Fragment>
              <FieldImagesFromPdf
                documentVersionFields={allDocumentVersionFields}
                pdfUrl={selectedDocument.fileUrl}
                onImagesLoaded={handleImagesLoaded}
              />
            </Fragment>
          )}

        {/* Document Review Buttons - show for external variant when documents need review or already have submitted reviews */}
        {shouldShowReviewButtons && (
          <Flex gap={2}>
            <Button
              flex={1}
              isDisabled={
                reviewForButtonState?.status ===
                IngestionDocumentReviewStatus.RETURNED
              }
              size="sm"
              variant={
                reviewForButtonState?.status ===
                IngestionDocumentReviewStatus.RETURNED
                  ? 'solid'
                  : 'outline'
              }
              onClick={() => void handleReturnDocument()}
            >
              Return
            </Button>
            <Button
              flex={1}
              isDisabled={
                reviewForButtonState?.status ===
                IngestionDocumentReviewStatus.APPROVED
              }
              size="sm"
              variant={
                reviewForButtonState?.status ===
                IngestionDocumentReviewStatus.APPROVED
                  ? 'solid'
                  : 'outline'
              }
              onClick={() => void handleApproveDocument()}
            >
              Approve
            </Button>
          </Flex>
        )}

        <KeyTermsCard
          folder={folder || undefined}
          keyTerms={folder?.keyTerms}
          mlsListing={folder?.mlsListing}
          variant={variant}
        />

        {!validationForSelectedDocument && (
          <Text color="compliance.text.muted">
            {emptyValidationsDescription}
          </Text>
        )}
        <>
          {loading && !validationData?.documentValidations && <CenterSpinner />}
          {/* Contract Document Validation Results - each contract doc gets its own sections */}

          {Array.from(validationResultsForSelectedDocument.entries()).map(
            ([
              contractDocId,
              {
                contractDocument,
                errors: contractErrors,
                successes: contractSuccesses,
                userAddedIssues,
                pageIssueMessage,
              },
            ]) => {
              const contractDocName =
                getContractDocDisplayName(contractDocument);

              const hasErrors =
                contractErrors.length > 0 || userAddedIssues.length > 0;

              return (
                <VStack key={contractDocId} align="stretch" spacing={2}>
                  {/* Contract Document Header */}
                  <Heading
                    color="compliance.text.secondary"
                    size="smallUppercase"
                  >
                    {contractDocName}
                  </Heading>

                  {/* Issues Section for this contract document */}
                  {pageIssueMessage && (
                    <Alert
                      alignItems="flex-start"
                      borderRadius="md"
                      gap={2}
                      p={3}
                      pl={2}
                      pr={4}
                      status="warning"
                      variant="subtle"
                    >
                      <GradientIcon icon={Rabbit} mt={1} />
                      <VStack align="stretch" spacing={1}>
                        <AlertDescription fontSize="sm">
                          {pageIssueMessage}
                        </AlertDescription>
                      </VStack>
                    </Alert>
                  )}

                  {/* Issues Section for this contract document */}
                  {(hasErrors ||
                    (variant === 'external' &&
                      Boolean(onAddDocumentIssue))) && (
                    <CollapsibleSection
                      cardHeaderChildren={
                        isExtension && hasErrors ? (
                          <Box ml={2}>
                            <CopyValidationErrors
                              contractDocumentUuid={contractDocId}
                              document={selectedDocument}
                              hiddenIssues={hiddenDocumentValidationReviews}
                              isAddedToMessage={manualErrorGroups.some(
                                (g) =>
                                  g.document.id === selectedDocument.id &&
                                  g.contractDocumentUuid === contractDocId
                              )}
                              isDocumentReviewed={reviewedDocuments.some(
                                (r) => r.document.id === selectedDocument.id
                              )}
                              platform={folder?.platform}
                              userAddedIssues={userAddedIssues}
                              validationData={validationData}
                              onAddToMessage={onAddErrorGroupToMessage}
                            />
                          </Box>
                        ) : null
                      }
                      isExpanded={sectionStates.isErrorsExpanded}
                      showAddButton={
                        variant === 'external' && Boolean(onAddDocumentIssue)
                      }
                      title={
                        <Heading size="smallUppercase">
                          Validation Issues (
                          {getVisibleIssuesCount(
                            contractErrors,
                            userAddedIssues,
                            contractDocId,
                            hiddenDocumentValidationReviews
                          )}
                          )
                        </Heading>
                      }
                      onAddClick={() => handleOpenAddIssueModal(contractDocId)}
                      onShowHideValidationErrors={onShowHideValidationErrors}
                      onToggle={sectionActions.toggleErrors}
                    >
                      <VStack align="stretch" spacing={0}>
                        {/* Empty state when no issues exist */}
                        {!hasErrors && (
                          <Text color="compliance.text.muted" fontSize="sm">
                            No validation issues
                          </Text>
                        )}

                        {/* User Added Issues - displayed first */}
                        {userAddedIssues.map((issue) => {
                          const rowKey = `user-contract-${contractDocId}-${issue.id}`;
                          const isHidden = issue.isHidden;

                          return (
                            <Flex
                              key={issue.id}
                              align="center"
                              cursor={
                                onDeleteDocumentIssue ? 'pointer' : 'default'
                              }
                              gap={2}
                              justify="space-between"
                              opacity={isHidden ? 0.6 : 1}
                              px={1}
                              py={2}
                              onMouseEnter={
                                onDeleteDocumentIssue
                                  ? () => setHoveredRowIndex(rowKey)
                                  : undefined
                              }
                              onMouseLeave={
                                onDeleteDocumentIssue
                                  ? () => setHoveredRowIndex(null)
                                  : undefined
                              }
                            >
                              <Flex align="center" gap={2} overflow="hidden">
                                <Tag
                                  borderRadius="xs"
                                  color={
                                    isHidden
                                      ? 'compliance.text.muted'
                                      : undefined
                                  }
                                  size="sm"
                                >
                                  <Text fontSize="xs">Custom</Text>
                                </Tag>
                                <Tooltip
                                  bg="compliance.tooltip.bg"
                                  borderColor="compliance.tooltip.border"
                                  borderWidth={1}
                                  color="compliance.tooltip.text"
                                  label={issue.message || 'User added issue'}
                                >
                                  <Text
                                    color={
                                      isHidden
                                        ? 'compliance.text.muted'
                                        : undefined
                                    }
                                    fontSize="sm"
                                    fontWeight="medium"
                                    overflow="hidden"
                                    textDecoration={
                                      isHidden ? 'line-through' : undefined
                                    }
                                    textOverflow="ellipsis"
                                    whiteSpace="nowrap"
                                  >
                                    {issue.message || 'User added issue'}
                                  </Text>
                                </Tooltip>
                              </Flex>
                              {onDeleteDocumentIssue && (
                                <IconButton
                                  aria-label="Delete issue"
                                  color="compliance.text.muted"
                                  icon={<Icon as={X} />}
                                  size="xxs"
                                  variant="link"
                                  onClick={() =>
                                    void handleDeleteDocumentIssue(issue.id)
                                  }
                                />
                              )}
                            </Flex>
                          );
                        })}

                        {/* Regular validation errors - displayed after user issues */}
                        {contractErrors.map((error, index) => {
                          const rowKey = `error-${contractDocId}-${index}`;
                          const props = {
                            allFeedback,
                            allHiddenReviews: hiddenDocumentValidationReviews,
                            hoveredRowIndex,
                            rowKey,
                            item: error,
                            selectedDocument,
                            type: 'error' as const,
                            variant,
                            onHideDocumentValidation,
                            onItemClick: handleItemClick,
                            onMouseEnter: () => setHoveredRowIndex(rowKey),
                            onMouseLeave: () => setHoveredRowIndex(null),
                          };

                          if (variant === 'internal') {
                            return (
                              <InternalValidationItem
                                key={`error-${contractDocId}-${error.ruleUuid}-${index}`}
                                {...props}
                                diffData={itemsDiffData?.get(
                                  `error-${error.ruleUuid}`
                                )}
                                fieldsToImages={fieldsToImages}
                              />
                            );
                          }

                          return (
                            <BaseValidationItem
                              key={`error-${contractDocId}-${error.ruleUuid}-${index}`}
                              {...props}
                            />
                          );
                        })}
                      </VStack>
                    </CollapsibleSection>
                  )}

                  {/* Successful Checks Section for this contract document */}
                  {contractSuccesses.length > 0 && (
                    <CollapsibleSection
                      isExpanded={sectionStates.isSuccessesExpanded}
                      title={
                        <Heading size="smallUppercase">
                          Successful Checks ({contractSuccesses.length})
                        </Heading>
                      }
                      onToggle={sectionActions.toggleSuccesses}
                    >
                      <VStack align="stretch" spacing={0}>
                        {contractSuccesses.map((success, index) => {
                          const rowKey = `success-${contractDocId}-${index}`;
                          const props = {
                            allFeedback,
                            allHiddenReviews: hiddenDocumentValidationReviews,
                            hoveredRowIndex,
                            rowKey,
                            item: success,
                            selectedDocument,
                            type: 'success' as const,
                            variant,
                            onHideDocumentValidation,
                            onItemClick: handleItemClick,
                            onMouseEnter: () => setHoveredRowIndex(rowKey),
                            onMouseLeave: () => setHoveredRowIndex(null),
                          };

                          if (variant === 'internal') {
                            return (
                              <InternalValidationItem
                                key={`success-${contractDocId}-${success.ruleUuid}-${index}`}
                                {...props}
                                diffData={itemsDiffData?.get(
                                  `success-${success.ruleUuid}`
                                )}
                                fieldsToImages={fieldsToImages}
                              />
                            );
                          }

                          return (
                            <BaseValidationItem
                              key={`success-${contractDocId}-${success.ruleUuid}-${index}`}
                              {...props}
                            />
                          );
                        })}
                      </VStack>
                    </CollapsibleSection>
                  )}
                </VStack>
              );
            }
          )}
        </>

        {/* [MARK] INTERNAL ONLY DEBUG SECTIONS */}
        {variant === 'internal' && (
          <>
            <CollapsibleSection
              isDebugSection
              isExpanded={sectionStates.isFolderExpanded}
              title="Folder Information"
              onToggle={sectionActions.toggleFolder}
            >
              <FolderInformation folder={folder} />
            </CollapsibleSection>

            <CollapsibleSection
              isDebugSection
              cardHeaderChildren={
                <Tooltip
                  hasArrow
                  label={<PipelineStageSelectionTooltipContent />}
                  placement="left"
                >
                  <IconButton
                    _hover={{ opacity: 1 }}
                    aria-label="Pipeline selection info"
                    color="compliance.debug.text"
                    icon={<InfoOutlineIcon />}
                    opacity={0.6}
                    size="xs"
                    variant="ghost"
                  />
                </Tooltip>
              }
              isExpanded={sectionStates.isPipelineExpanded}
              title="Pipeline & Stage Selection"
              onToggle={sectionActions.togglePipeline}
            >
              <PipelineStageSelectionInfo folder={folder} />
            </CollapsibleSection>

            <CollapsibleSection
              isDebugSection
              isExpanded={sectionStates.isDebugExpanded}
              title="Debug Information"
              onToggle={sectionActions.toggleDebug}
            >
              <DebugInfoMetrics
                folder={folder}
                selectedDocument={selectedDocument}
                validationResultsByContract={
                  validationResultsForSelectedDocument
                }
              />
            </CollapsibleSection>

            <CollapsibleSection
              isDebugSection
              isExpanded={sectionStates.isTimestampsExpanded}
              title="Validation Timestamps"
              onToggle={sectionActions.toggleTimestamps}
            >
              {validationTimestamps ? (
                <TimestampMetrics timestampData={validationTimestamps} />
              ) : (
                <Box bg="compliance.debug.code.bg" borderRadius="md" p={3}>
                  <Text color="compliance.debug.code.text.warning">
                    No timestamp data available
                  </Text>
                </Box>
              )}
            </CollapsibleSection>
            {selectedDocument.contractDocuments &&
              selectedDocument.contractDocuments.length > 0 &&
              (selectedDocErrors.length > 0 ||
                selectedDocSuccesses.length > 0) && (
                <CollapsibleSection
                  isDebugSection
                  isExpanded={sectionStates.isAIComparisonExpanded}
                  title="AI vs Regular Validation"
                  onToggle={sectionActions.toggleAIComparison}
                >
                  {comparisonData ? (
                    <AIComparisonMetrics comparisonData={comparisonData} />
                  ) : (
                    <Box bg="compliance.debug.code.bg" borderRadius="md" p={3}>
                      <Text color="compliance.debug.code.text.warning">
                        No AI comparison data available
                      </Text>
                    </Box>
                  )}
                </CollapsibleSection>
              )}

            {/* Classification section */}
            <CollapsibleSection
              isDebugSection
              isExpanded={sectionStates.isClassificationExpanded}
              title="Document Classification"
              onToggle={sectionActions.toggleClassification}
            >
              <ClassificationDetails selectedDocument={selectedDocument} />
            </CollapsibleSection>

            {/* Extracted Fields section */}
            <CollapsibleSection
              isDebugSection
              isExpanded={sectionStates.isExtractedFieldsExpanded}
              title="Merged Extracted Fields"
              onToggle={sectionActions.toggleExtractedFields}
            >
              <ExtractedFieldsList
                selectedDocument={selectedDocument}
                onFieldClick={handleItemClick}
              />
            </CollapsibleSection>
          </>
        )}
      </VStack>

      {onAddDocumentIssue && (
        <AddIssueModal
          isOpen={isAddIssueModalOpen}
          issueType={IssueType.DOCUMENT}
          onClose={handleCloseAddIssueModal}
          onSubmit={handleAddDocumentIssue}
        />
      )}
    </Box>
  );
};
